/**
 * Offline EEHA Storage Service
 *
 * Uses IndexedDB to store EEHA inspection data offline.
 * Supports photo attachments and equipment caching for field work.
 */

import type { EEHAInspection, EEHAEquipment, EEHAConfig, EEHAResolvedConfig, WorkpackDownloadBundle } from '../types/eeha';

const DB_NAME = 'GRAutomates_EEHA_Offline';
const DB_VERSION = 2;
const STORE_INSPECTIONS = 'eehaInspections';
const STORE_EQUIPMENT = 'eehaEquipmentCache';
const STORE_CONFIG = 'eehaConfig';
const STORE_WORKPACKS = 'eehaWorkpacks';

// ============================================================================
// Database Initialization
// ============================================================================

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Store for offline inspections
      if (!db.objectStoreNames.contains(STORE_INSPECTIONS)) {
        const inspectionStore = db.createObjectStore(STORE_INSPECTIONS, {
          keyPath: 'localId',
          autoIncrement: true,
        });
        inspectionStore.createIndex('timestamp', 'timestamp', { unique: false });
        inspectionStore.createIndex('equipmentId', 'equipmentId', { unique: false });
        inspectionStore.createIndex('status', 'status', { unique: false });
      }

      // Store for cached equipment (for offline selection)
      if (!db.objectStoreNames.contains(STORE_EQUIPMENT)) {
        const equipmentStore = db.createObjectStore(STORE_EQUIPMENT, { keyPath: '_id' });
        equipmentStore.createIndex('tagNo', 'tagNo', { unique: false });
        equipmentStore.createIndex('clientName', 'clientName', { unique: false });
      }

      // Store for config data (clients, dropdowns, checklist templates)
      if (!db.objectStoreNames.contains(STORE_CONFIG)) {
        db.createObjectStore(STORE_CONFIG, { keyPath: 'key' });
      }

      // Store for cached workpack bundles (v2)
      if (!db.objectStoreNames.contains(STORE_WORKPACKS)) {
        const wpStore = db.createObjectStore(STORE_WORKPACKS, { keyPath: '_id' });
        wpStore.createIndex('workpackNo', 'workpackNo', { unique: false });
      }
    };
  });
}

// ============================================================================
// Config Cache
// ============================================================================

/**
 * Cache EEHA config for offline use
 */
export async function cacheConfig(config: EEHAConfig): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_CONFIG, 'readwrite');
    const store = tx.objectStore(STORE_CONFIG);

    await new Promise<void>((resolve, reject) => {
      const request = store.put({
        key: 'eehaConfig',
        data: config,
        cachedAt: new Date().toISOString(),
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
    console.info('[OfflineEEHA] Config cached successfully');
  } catch (error) {
    console.error('[OfflineEEHA] Failed to cache config:', error);
    throw error;
  }
}

/**
 * Get cached config
 */
export async function getCachedConfig(): Promise<EEHAConfig | null> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_CONFIG, 'readonly');
    const store = tx.objectStore(STORE_CONFIG);

    const result: any = await new Promise((resolve, reject) => {
      const request = store.get('eehaConfig');
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    db.close();
    return result?.data || null;
  } catch (error) {
    console.error('[OfflineEEHA] Failed to get cached config:', error);
    return null;
  }
}

/**
 * Cache scoped config (dropdown options + fault categories) for a client/site.
 * Keyed by client name and optional site name in the same eehaConfig store.
 */
export async function cacheScopedConfig(
  clientName: string,
  siteName: string | undefined,
  data: EEHAResolvedConfig
): Promise<void> {
  try {
    const key = siteName
      ? `scopedConfig:${clientName}:${siteName}`
      : `scopedConfig:${clientName}`;
    const db = await openDB();
    const tx = db.transaction(STORE_CONFIG, 'readwrite');
    const store = tx.objectStore(STORE_CONFIG);

    await new Promise<void>((resolve, reject) => {
      const request = store.put({
        key,
        data,
        cachedAt: new Date().toISOString(),
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
    console.info(`[OfflineEEHA] Scoped config cached: ${key}`);
  } catch (error) {
    console.error('[OfflineEEHA] Failed to cache scoped config:', error);
  }
}

/**
 * Get cached scoped config for a client/site.
 * Tries site-level key first, then falls back to client-level.
 */
export async function getCachedScopedConfig(
  clientName: string,
  siteName: string | undefined
): Promise<EEHAResolvedConfig | null> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_CONFIG, 'readonly');
    const store = tx.objectStore(STORE_CONFIG);

    // Try site-level first
    if (siteName) {
      const siteResult: any = await new Promise((resolve, reject) => {
        const request = store.get(`scopedConfig:${clientName}:${siteName}`);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      if (siteResult?.data) {
        db.close();
        return siteResult.data;
      }
    }

    // Fall back to client-level
    const clientResult: any = await new Promise((resolve, reject) => {
      const request = store.get(`scopedConfig:${clientName}`);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    db.close();
    return clientResult?.data || null;
  } catch (error) {
    console.error('[OfflineEEHA] Failed to get cached scoped config:', error);
    return null;
  }
}

/**
 * Check if config is cached
 */
export async function hasConfig(): Promise<boolean> {
  try {
    const config = await getCachedConfig();
    return !!config;
  } catch {
    return false;
  }
}

// ============================================================================
// Equipment Cache
// ============================================================================

/**
 * Cache equipment list for offline selection
 */
export async function cacheEquipmentList(equipment: EEHAEquipment[]): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_EQUIPMENT, 'readwrite');
    const store = tx.objectStore(STORE_EQUIPMENT);

    // Clear existing cache
    await new Promise<void>((resolve, reject) => {
      const clearRequest = store.clear();
      clearRequest.onsuccess = () => resolve();
      clearRequest.onerror = () => reject(clearRequest.error);
    });

    // Add all equipment
    for (const item of equipment) {
      await new Promise<void>((resolve, reject) => {
        const request = store.add(item);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    }

    db.close();
    console.info(`[OfflineEEHA] Cached ${equipment.length} equipment items`);
  } catch (error) {
    console.error('[OfflineEEHA] Failed to cache equipment:', error);
    throw error;
  }
}

/**
 * Get cached equipment list
 */
export async function getCachedEquipment(): Promise<EEHAEquipment[]> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_EQUIPMENT, 'readonly');
    const store = tx.objectStore(STORE_EQUIPMENT);

    const equipment: EEHAEquipment[] = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    db.close();
    return equipment || [];
  } catch (error) {
    console.error('[OfflineEEHA] Failed to get cached equipment:', error);
    return [];
  }
}

/**
 * Get single equipment by ID from cache
 */
export async function getCachedEquipmentById(id: string): Promise<EEHAEquipment | null> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_EQUIPMENT, 'readonly');
    const store = tx.objectStore(STORE_EQUIPMENT);

    const equipment: EEHAEquipment | undefined = await new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    db.close();
    return equipment || null;
  } catch (error) {
    console.error('[OfflineEEHA] Failed to get equipment by ID:', error);
    return null;
  }
}

/**
 * Search cached equipment
 */
export async function searchCachedEquipment(query: string): Promise<EEHAEquipment[]> {
  try {
    const allEquipment = await getCachedEquipment();
    const lowerQuery = query.toLowerCase();

    return allEquipment.filter(
      (item) =>
        item.tagNo?.toLowerCase().includes(lowerQuery) ||
        item.equipmentNo?.toLowerCase().includes(lowerQuery) ||
        item.description?.toLowerCase().includes(lowerQuery) ||
        item.location?.toLowerCase().includes(lowerQuery)
    );
  } catch (error) {
    console.error('[OfflineEEHA] Failed to search equipment:', error);
    return [];
  }
}

// ============================================================================
// Inspection Storage
// ============================================================================

export interface OfflineInspectionPhoto {
  blob: Blob;
  originalName: string;
  mimeType: string;
  size: number;
  category?: string;
  itemCode?: string;
  caption?: string;
}

export interface OfflineInspection extends Omit<Partial<EEHAInspection>, 'photos'> {
  localId?: number;
  timestamp: string;
  offlineMode: true;
  photos: OfflineInspectionPhoto[];
  lastModified?: string;
}

/**
 * Save inspection offline
 */
export async function saveInspection(
  inspectionData: Partial<EEHAInspection>,
  photos: Array<{ file: File; category?: string; itemCode?: string; caption?: string }> = []
): Promise<number> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_INSPECTIONS, 'readwrite');
    const store = tx.objectStore(STORE_INSPECTIONS);

    // Convert photos to blobs with metadata
    const photoBlobs: OfflineInspectionPhoto[] = await Promise.all(
      photos.map(async (photo) => {
        const arrayBuffer = await photo.file.arrayBuffer();
        return {
          blob: new Blob([arrayBuffer], { type: photo.file.type }),
          originalName: photo.file.name,
          mimeType: photo.file.type,
          size: photo.file.size,
          category: photo.category || '',
          itemCode: photo.itemCode || '',
          caption: photo.caption || '',
        };
      })
    );

    const offlineInspection: OfflineInspection = {
      ...inspectionData,
      timestamp: new Date().toISOString(),
      offlineMode: true,
      photos: photoBlobs,
    };

    const localId = await new Promise<number>((resolve, reject) => {
      const request = store.add(offlineInspection);
      request.onsuccess = () => resolve(request.result as number);
      request.onerror = () => reject(request.error);
    });

    db.close();
    console.info('[OfflineEEHA] Inspection saved with localId:', localId);

    // Auto-backup to OPFS for redundancy (non-blocking)
    offlineInspection.localId = localId;
    backupInspectionToFile(localId, offlineInspection).catch(() => {});

    return localId;
  } catch (error) {
    console.error('[OfflineEEHA] Failed to save inspection:', error);
    throw error;
  }
}

/**
 * Update existing offline inspection
 */
export async function updateInspection(
  localId: number,
  inspectionData: Partial<EEHAInspection>,
  photos: Array<{ file: File; category?: string; itemCode?: string; caption?: string }> = []
): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_INSPECTIONS, 'readwrite');
    const store = tx.objectStore(STORE_INSPECTIONS);

    // Get existing inspection
    const existing: OfflineInspection | undefined = await new Promise((resolve, reject) => {
      const request = store.get(localId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (!existing) {
      db.close();
      throw new Error('Inspection not found');
    }

    // Convert new photos to blobs
    const photoBlobs: OfflineInspectionPhoto[] = await Promise.all(
      photos.map(async (photo) => {
        const arrayBuffer = await photo.file.arrayBuffer();
        return {
          blob: new Blob([arrayBuffer], { type: photo.file.type }),
          originalName: photo.file.name,
          mimeType: photo.file.type,
          size: photo.file.size,
          category: photo.category || '',
          itemCode: photo.itemCode || '',
          caption: photo.caption || '',
        };
      })
    );

    const updatedInspection: OfflineInspection = {
      ...existing,
      ...inspectionData,
      localId,
      photos: photoBlobs.length > 0 ? photoBlobs : existing.photos,
      lastModified: new Date().toISOString(),
      offlineMode: true,
      timestamp: existing.timestamp,
    };

    await new Promise<void>((resolve, reject) => {
      const request = store.put(updatedInspection);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
    console.info('[OfflineEEHA] Inspection updated:', localId);

    // Auto-backup to OPFS for redundancy (non-blocking)
    backupInspectionToFile(localId, updatedInspection).catch(() => {});
  } catch (error) {
    console.error('[OfflineEEHA] Failed to update inspection:', error);
    throw error;
  }
}

/**
 * Get all offline inspections
 */
export async function getInspections(): Promise<OfflineInspection[]> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_INSPECTIONS, 'readonly');
    const store = tx.objectStore(STORE_INSPECTIONS);

    const inspections: OfflineInspection[] = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    db.close();
    return inspections || [];
  } catch (error) {
    console.error('[OfflineEEHA] Failed to get inspections:', error);
    return [];
  }
}

/**
 * Get single offline inspection by localId
 */
export async function getInspection(localId: number): Promise<OfflineInspection | null> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_INSPECTIONS, 'readonly');
    const store = tx.objectStore(STORE_INSPECTIONS);

    const inspection: OfflineInspection | undefined = await new Promise((resolve, reject) => {
      const request = store.get(localId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    db.close();
    return inspection || null;
  } catch (error) {
    console.error('[OfflineEEHA] Failed to get inspection:', error);
    return null;
  }
}

/**
 * Delete offline inspection
 */
export async function deleteInspection(localId: number): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_INSPECTIONS, 'readwrite');
    const store = tx.objectStore(STORE_INSPECTIONS);

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(localId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
    console.info('[OfflineEEHA] Inspection deleted:', localId);
  } catch (error) {
    console.error('[OfflineEEHA] Failed to delete inspection:', error);
    throw error;
  }
}

/**
 * Get count of offline inspections
 */
export async function getInspectionCount(): Promise<number> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_INSPECTIONS, 'readonly');
    const store = tx.objectStore(STORE_INSPECTIONS);

    const count: number = await new Promise((resolve, reject) => {
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    db.close();
    return count;
  } catch (error) {
    console.error('[OfflineEEHA] Failed to get inspection count:', error);
    return 0;
  }
}

/**
 * Clear all synced inspections
 */
export async function clearSyncedInspections(): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_INSPECTIONS, 'readwrite');
    const store = tx.objectStore(STORE_INSPECTIONS);

    await new Promise<void>((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
    console.info('[OfflineEEHA] All inspections cleared');
  } catch (error) {
    console.error('[OfflineEEHA] Failed to clear inspections:', error);
    throw error;
  }
}

// ============================================================================
// Persistent Storage & Protection
// ============================================================================

/**
 * Request persistent storage so the browser won't auto-evict IndexedDB.
 * Returns true if granted, false if denied or unavailable.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (navigator.storage && navigator.storage.persist) {
      const granted = await navigator.storage.persist();
      console.info(`[OfflineEEHA] Persistent storage ${granted ? 'granted' : 'denied'}`);
      return granted;
    }
    return false;
  } catch (error) {
    console.error('[OfflineEEHA] Failed to request persistent storage:', error);
    return false;
  }
}

/**
 * Check if persistent storage is currently granted.
 */
export async function isPersistentStorage(): Promise<boolean> {
  try {
    if (navigator.storage && navigator.storage.persisted) {
      return await navigator.storage.persisted();
    }
    return false;
  } catch {
    return false;
  }
}

// ============================================================================
// Sync Utilities
// ============================================================================

/**
 * Get storage estimate using the browser Storage API for accuracy.
 */
export async function getStorageEstimate(): Promise<{
  inspectionCount: number;
  equipmentCount: number;
  estimatedSize: number;
  estimatedSizeMB: string;
  quotaMB: string;
  usagePercent: number;
  isPersistent: boolean;
  fileBackupCount: number;
}> {
  try {
    const [inspections, equipment, persistent, backupCount] = await Promise.all([
      getInspections(),
      getCachedEquipment(),
      isPersistentStorage(),
      getFileBackupCount(),
    ]);

    // Use browser Storage API for accurate usage when available
    let totalSize = 0;
    let quotaMB = 'Unknown';
    let usagePercent = 0;

    if (navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      totalSize = estimate.usage || 0;
      const quota = estimate.quota || 0;
      quotaMB = quota > 0 ? (quota / (1024 * 1024)).toFixed(0) : 'Unknown';
      usagePercent = quota > 0 ? Math.round((totalSize / quota) * 100) : 0;
    } else {
      // Fallback: rough estimate
      const inspectionSize = inspections.reduce((sum, insp) => {
        const photoSize = (insp.photos || []).reduce((s, p) => s + (p.size || 0), 0);
        return sum + photoSize + 1000;
      }, 0);
      totalSize = inspectionSize + equipment.length * 2000;
    }

    return {
      inspectionCount: inspections.length,
      equipmentCount: equipment.length,
      estimatedSize: totalSize,
      estimatedSizeMB: (totalSize / (1024 * 1024)).toFixed(1),
      quotaMB,
      usagePercent,
      isPersistent: persistent,
      fileBackupCount: backupCount,
    };
  } catch (error) {
    console.error('[OfflineEEHA] Failed to estimate storage:', error);
    return {
      inspectionCount: 0,
      equipmentCount: 0,
      estimatedSize: 0,
      estimatedSizeMB: '0.0',
      quotaMB: 'Unknown',
      usagePercent: 0,
      isPersistent: false,
      fileBackupCount: 0,
    };
  }
}

/**
 * Check if we have any pending offline data
 */
export async function hasPendingData(): Promise<boolean> {
  try {
    const count = await getInspectionCount();
    return count > 0;
  } catch {
    return false;
  }
}

/**
 * Prepare inspections for sync (convert blobs to base64)
 */
export async function prepareInspectionsForSync(): Promise<
  Array<{
    localId: number;
    inspection: Partial<EEHAInspection>;
    photos: Array<{
      base64Data: string;
      originalName: string;
      mimeType: string;
      category?: string;
      itemCode?: string;
      caption?: string;
    }>;
  }>
> {
  const inspections = await getInspections();

  const prepared = await Promise.all(
    inspections.map(async (insp) => {
      const photos = await Promise.all(
        (insp.photos || []).map(async (photo) => {
          const arrayBuffer = await photo.blob.arrayBuffer();
          const base64 = btoa(
            new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
          );
          return {
            base64Data: `data:${photo.mimeType};base64,${base64}`,
            originalName: photo.originalName,
            mimeType: photo.mimeType,
            category: photo.category,
            itemCode: photo.itemCode,
            caption: photo.caption,
          };
        })
      );

      // Remove offline-specific fields
      const { localId, timestamp, offlineMode, photos: _, lastModified, ...inspectionData } = insp;

      return {
        localId: localId || 0,
        inspection: inspectionData,
        photos,
      };
    })
  );

  return prepared;
}

// ============================================================================
// Workpack Cache
// ============================================================================

/** Shape stored in IDB - bundle plus a top-level _id for keyPath */
interface CachedWorkpack extends WorkpackDownloadBundle {
  _id: string;
  workpackNo: string;
  cachedAt: string;
}

/**
 * Cache a downloaded workpack bundle into IndexedDB.
 * Also extracts equipment items into the equipment cache.
 */
export async function cacheWorkpackBundle(bundle: WorkpackDownloadBundle): Promise<void> {
  try {
    const workpackId = String(bundle.workpack._id);
    const db = await openDB();
    const tx = db.transaction([STORE_WORKPACKS, STORE_EQUIPMENT], 'readwrite');
    const wpStore = tx.objectStore(STORE_WORKPACKS);
    const eqStore = tx.objectStore(STORE_EQUIPMENT);

    const cached: CachedWorkpack = {
      ...bundle,
      _id: workpackId,
      workpackNo: bundle.workpack.workpackNo || '',
      cachedAt: new Date().toISOString(),
    };

    // Store workpack bundle
    await new Promise<void>((resolve, reject) => {
      const request = wpStore.put(cached);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    // Extract and cache equipment from items
    for (const item of bundle.items) {
      if (item.equipment && item.equipment._id) {
        await new Promise<void>((resolve, reject) => {
          const request = eqStore.put(item.equipment);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      }
    }

    db.close();
    console.info(`[OfflineEEHA] Workpack ${bundle.workpack.workpackNo} cached (${bundle.items.length} items)`);
  } catch (error) {
    console.error('[OfflineEEHA] Failed to cache workpack:', error);
    throw error;
  }
}

/**
 * Get a cached workpack bundle by ID
 */
export async function getCachedWorkpack(workpackId: string): Promise<WorkpackDownloadBundle | null> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_WORKPACKS, 'readonly');
    const store = tx.objectStore(STORE_WORKPACKS);

    const result: CachedWorkpack | undefined = await new Promise((resolve, reject) => {
      const request = store.get(workpackId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    db.close();
    return result || null;
  } catch (error) {
    console.error('[OfflineEEHA] Failed to get cached workpack:', error);
    return null;
  }
}

/**
 * List all cached workpacks (summary info only)
 */
export async function getCachedWorkpackList(): Promise<
  Array<{
    _id: string;
    workpackNo: string;
    name: string;
    status: string;
    siteName: string;
    cachedAt: string;
    itemCount: number;
    completedCount: number;
  }>
> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_WORKPACKS, 'readonly');
    const store = tx.objectStore(STORE_WORKPACKS);

    const all: CachedWorkpack[] = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    db.close();
    return (all || []).map((wp) => ({
      _id: wp._id,
      workpackNo: wp.workpackNo,
      name: wp.workpack.name || '',
      status: (wp.workpack.status as string) || 'Unknown',
      siteName: (wp.workpack as Record<string, unknown>).siteName as string || '',
      cachedAt: wp.cachedAt,
      itemCount: wp.items.length,
      completedCount: wp.items.filter((i) => i.status === 'Completed' || i.status === 'Skipped').length,
    }));
  } catch (error) {
    console.error('[OfflineEEHA] Failed to list cached workpacks:', error);
    return [];
  }
}

/**
 * Update a single item within a cached workpack bundle
 */
export async function updateCachedWorkpackItem(
  workpackId: string,
  tagId: string,
  updates: Record<string, unknown>
): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_WORKPACKS, 'readwrite');
    const store = tx.objectStore(STORE_WORKPACKS);

    const existing: CachedWorkpack | undefined = await new Promise((resolve, reject) => {
      const request = store.get(workpackId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (!existing) {
      db.close();
      return;
    }

    // Find and update the matching item
    const itemIndex = existing.items.findIndex((i) => i.tagId === tagId);
    if (itemIndex >= 0) {
      existing.items[itemIndex] = Object.assign({}, existing.items[itemIndex], updates) as typeof existing.items[number];
    }

    await new Promise<void>((resolve, reject) => {
      const request = store.put(existing);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
    console.info(`[OfflineEEHA] Updated workpack item ${tagId} in ${workpackId}`);
  } catch (error) {
    console.error('[OfflineEEHA] Failed to update cached workpack item:', error);
  }
}

/**
 * Remove a cached workpack (after check-in)
 */
export async function removeCachedWorkpack(workpackId: string): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_WORKPACKS, 'readwrite');
    const store = tx.objectStore(STORE_WORKPACKS);

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(workpackId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
    console.info(`[OfflineEEHA] Removed cached workpack ${workpackId}`);
  } catch (error) {
    console.error('[OfflineEEHA] Failed to remove cached workpack:', error);
    throw error;
  }
}

// ============================================================================
// OPFS File Backup (redundant storage for inspection data)
// ============================================================================

const BACKUP_DIR = 'eeha-inspection-backups';

/**
 * Check if OPFS (Origin Private File System) is available
 */
function hasOPFS(): boolean {
  return typeof navigator !== 'undefined' && 'storage' in navigator && 'getDirectory' in navigator.storage;
}

/**
 * Get or create the backup directory handle in OPFS
 */
async function getBackupDir(): Promise<FileSystemDirectoryHandle | null> {
  if (!hasOPFS()) return null;
  try {
    const root = await navigator.storage.getDirectory();
    return await root.getDirectoryHandle(BACKUP_DIR, { create: true });
  } catch (error) {
    console.error('[OfflineEEHA] OPFS backup dir error:', error);
    return null;
  }
}

/**
 * Backup a single inspection to OPFS as a JSON file.
 * Called automatically when saving/updating inspections.
 * Photos are stored as base64 strings in the backup (blobs are converted).
 */
export async function backupInspectionToFile(localId: number, inspection: OfflineInspection): Promise<boolean> {
  const dir = await getBackupDir();
  if (!dir) return false;

  try {
    // Convert photo blobs to base64 for file storage
    const photosAsBase64 = await Promise.all(
      (inspection.photos || []).map(async (photo) => {
        try {
          const arrayBuffer = await photo.blob.arrayBuffer();
          const base64 = btoa(
            new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
          );
          return {
            base64Data: base64,
            originalName: photo.originalName,
            mimeType: photo.mimeType,
            size: photo.size,
            category: photo.category,
            itemCode: photo.itemCode,
            caption: photo.caption,
          };
        } catch {
          return null;
        }
      })
    );

    const backupData = {
      localId,
      backedUpAt: new Date().toISOString(),
      inspection: {
        ...inspection,
        photos: photosAsBase64.filter(Boolean),
      },
    };

    const fileName = `inspection-${localId}.json`;
    const fileHandle = await dir.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(JSON.stringify(backupData));
    await writable.close();

    console.info(`[OfflineEEHA] Inspection ${localId} backed up to OPFS`);
    return true;
  } catch (error) {
    console.error(`[OfflineEEHA] Failed to backup inspection ${localId}:`, error);
    return false;
  }
}

/**
 * Remove a file backup after successful sync
 */
export async function clearFileBackup(localId: number): Promise<void> {
  const dir = await getBackupDir();
  if (!dir) return;

  try {
    await dir.removeEntry(`inspection-${localId}.json`);
    console.info(`[OfflineEEHA] Cleared file backup for inspection ${localId}`);
  } catch (_error) {
    // File may not exist, ignore
  }
}

/**
 * Get count of file backups
 */
export async function getFileBackupCount(): Promise<number> {
  const dir = await getBackupDir();
  if (!dir) return 0;

  try {
    let count = 0;
    for await (const _entry of (dir as any).values()) {
      count++;
    }
    return count;
  } catch {
    return 0;
  }
}

/**
 * List all file backups with summary info
 */
export async function listFileBackups(): Promise<Array<{
  localId: number;
  fileName: string;
  backedUpAt: string;
  tagNo: string;
  inspectionType: string;
  sizeKB: string;
}>> {
  const dir = await getBackupDir();
  if (!dir) return [];

  try {
    const backups: Array<{
      localId: number;
      fileName: string;
      backedUpAt: string;
      tagNo: string;
      inspectionType: string;
      sizeKB: string;
    }> = [];

    for await (const [name, handle] of (dir as any).entries()) {
      if (name.endsWith('.json') && handle.kind === 'file') {
        try {
          const file = await (handle as FileSystemFileHandle).getFile();
          const text = await file.text();
          const data = JSON.parse(text);
          backups.push({
            localId: data.localId,
            fileName: name,
            backedUpAt: data.backedUpAt || '',
            tagNo: data.inspection?.tagNo || '-',
            inspectionType: data.inspection?.inspectionType || '-',
            sizeKB: (file.size / 1024).toFixed(1),
          });
        } catch {
          // Skip corrupt files
        }
      }
    }

    return backups.sort((a, b) => b.localId - a.localId);
  } catch {
    return [];
  }
}

/**
 * Restore inspections from OPFS file backups into IndexedDB.
 * Used when IndexedDB data is lost but OPFS backups remain.
 * Returns count of restored inspections.
 */
export async function restoreFromFileBackups(): Promise<number> {
  const dir = await getBackupDir();
  if (!dir) return 0;

  let restored = 0;

  try {
    const existingInspections = await getInspections();
    const existingIds = new Set(existingInspections.map((i) => i.localId));

    for await (const [name, handle] of (dir as any).entries()) {
      if (!name.endsWith('.json') || handle.kind !== 'file') continue;

      try {
        const file = await (handle as FileSystemFileHandle).getFile();
        const text = await file.text();
        const data = JSON.parse(text);

        // Skip if already exists in IndexedDB
        if (existingIds.has(data.localId)) continue;

        const insp = data.inspection;
        if (!insp) continue;

        // Convert base64 photos back to blobs
        const photos: OfflineInspectionPhoto[] = (insp.photos || [])
          .filter((p: any) => p && p.base64Data)
          .map((p: any) => {
            const byteString = atob(p.base64Data);
            const ab = new Uint8Array(byteString.length);
            for (let i = 0; i < byteString.length; i++) {
              ab[i] = byteString.charCodeAt(i);
            }
            return {
              blob: new Blob([ab], { type: p.mimeType || 'application/octet-stream' }),
              originalName: p.originalName || 'photo',
              mimeType: p.mimeType || 'application/octet-stream',
              size: p.size || ab.length,
              category: p.category,
              itemCode: p.itemCode,
              caption: p.caption,
            };
          });

        // Save back to IndexedDB
        await saveInspection(
          { ...insp, photos: undefined },
          photos.map((p) => ({
            file: new File([p.blob], p.originalName, { type: p.mimeType }),
            category: p.category,
            itemCode: p.itemCode,
            caption: p.caption,
          }))
        );

        restored++;
      } catch (err) {
        console.error(`[OfflineEEHA] Failed to restore ${name}:`, err);
      }
    }

    console.info(`[OfflineEEHA] Restored ${restored} inspections from file backups`);
    return restored;
  } catch (error) {
    console.error('[OfflineEEHA] Failed to restore from file backups:', error);
    return 0;
  }
}

/**
 * Export all pending inspections as a downloadable JSON file.
 * Manual safety net the user can trigger.
 */
export async function exportInspectionsToFile(): Promise<void> {
  try {
    const inspections = await getInspections();
    if (inspections.length === 0) return;

    // Convert photo blobs to base64 for export
    const exportData = await Promise.all(
      inspections.map(async (insp) => {
        const photos = await Promise.all(
          (insp.photos || []).map(async (photo) => {
            try {
              const arrayBuffer = await photo.blob.arrayBuffer();
              const base64 = btoa(
                new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
              );
              return {
                base64Data: base64,
                originalName: photo.originalName,
                mimeType: photo.mimeType,
                size: photo.size,
                category: photo.category,
                itemCode: photo.itemCode,
                caption: photo.caption,
              };
            } catch {
              return null;
            }
          })
        );

        return {
          ...insp,
          photos: photos.filter(Boolean),
        };
      })
    );

    const blob = new Blob(
      [JSON.stringify({ exportedAt: new Date().toISOString(), inspections: exportData }, null, 2)],
      { type: 'application/json' }
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `eeha-inspections-backup-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);

    console.info(`[OfflineEEHA] Exported ${inspections.length} inspections to file`);
  } catch (error) {
    console.error('[OfflineEEHA] Failed to export inspections:', error);
    throw error;
  }
}

export default {
  // Config
  cacheConfig,
  getCachedConfig,
  cacheScopedConfig,
  getCachedScopedConfig,
  hasConfig,

  // Equipment
  cacheEquipmentList,
  getCachedEquipment,
  getCachedEquipmentById,
  searchCachedEquipment,

  // Inspections
  saveInspection,
  updateInspection,
  getInspections,
  getInspection,
  deleteInspection,
  getInspectionCount,
  clearSyncedInspections,

  // Workpacks
  cacheWorkpackBundle,
  getCachedWorkpack,
  getCachedWorkpackList,
  updateCachedWorkpackItem,
  removeCachedWorkpack,

  // Storage protection
  requestPersistentStorage,
  isPersistentStorage,

  // File backups (OPFS)
  backupInspectionToFile,
  clearFileBackup,
  getFileBackupCount,
  listFileBackups,
  restoreFromFileBackups,
  exportInspectionsToFile,

  // Utilities
  getStorageEstimate,
  hasPendingData,
  prepareInspectionsForSync,
};
